import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Paperclip, ClipboardList, Handshake } from "lucide-react";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled"];
const APP_STATUSES = ["new", "contacted", "onboarded", "declined"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings");
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([api.get("/admin/stats"), api.get("/admin/bookings")]);
      setStats(s.data); setBookings(b.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadPartners = async () => {
    setPartnersLoading(true);
    try {
      const { data } = await api.get("/admin/partner-applications");
      setPartners(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPartnersLoading(false); }
  };
  useEffect(() => { if (tab === "partners") loadPartners(); }, [tab]);

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      toast.success("Status updated");
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const updatePartner = async (id, status) => {
    try {
      await api.patch(`/admin/partner-applications/${id}`, { status });
      toast.success("Application updated");
      setPartners((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const filtered = bookings.filter((b) => (filter === "all" ? true : b.status === filter));

  return (
    <div data-testid="admin-page" className="bg-[#F5EFE4] min-h-[80vh]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-20">
        <p className="overline">Concierge Desk</p>
        <h1 className="font-serif text-5xl lg:text-6xl mt-4 text-[#1A1815]">Admin Ledger.</h1>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
          <Stat label="Total bookings" value={stats?.total_bookings ?? "—"} />
          <Stat label="Pending" value={stats?.pending_bookings ?? "—"} />
          <Stat label="Confirmed" value={stats?.confirmed_bookings ?? "—"} />
          <Stat label="Customers" value={stats?.total_customers ?? "—"} />
          <Stat label="Revenue" value={stats ? `£${Number(stats.total_revenue).toLocaleString()}` : "—"} highlight />
        </div>

        {/* Tabs */}
        <div className="mt-12 flex gap-3 border-b border-black/10">
          {[
            ["bookings", "Bookings", ClipboardList],
            ["partners", "Partner Applications", Handshake],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.22em] transition-colors border-b-2 ${tab === id ? "border-[#C5A059] text-[#1A1815]" : "border-transparent text-[#6B665E] hover:text-[#1A1815]"}`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (<>
        <div className="mt-6 flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
              className={`text-[10px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${filter === s ? "border-[#C5A059] bg-[#C5A059] text-white" : "border-[#E4D9C1] bg-white text-[#1A1815] hover:border-[#C5A059]"}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="mt-6 bg-white border border-[#E4D9C1]">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E4D9C1] text-[10px] uppercase tracking-[0.22em] text-[#6B665E]">
            <div className="col-span-3">Client</div><div className="col-span-2">Service</div><div className="col-span-2">Date · Time</div><div className="col-span-2">Amount</div><div className="col-span-1">Paid</div><div className="col-span-2">Status</div>
          </div>
          {loading ? <div className="p-10 text-[#6B665E]">Loading…</div> : filtered.length === 0 ? <div className="p-10 text-[#6B665E]">No bookings.</div> : (
            filtered.map((b) => (
              <div key={b.id} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#E4D9C1] items-center text-sm last:border-b-0" data-testid={`admin-row-${b.id}`}>
                <div className="col-span-3"><div className="text-[#1A1815] font-medium">{b.customer_name}</div><div className="text-xs text-[#6B665E]">{b.customer_email}</div></div>
                <div className="col-span-2"><div className="text-[#1A1815]">{b.service_name}</div><div className="text-xs text-[#6B665E] flex items-center gap-1.5">{b.package_name}{b.attachments?.length > 0 && (<span className="inline-flex items-center gap-0.5 text-[#C5A059]" data-testid={`admin-attach-${b.id}`}><Paperclip size={10} /> {b.attachments.length}</span>)}</div></div>
                <div className="col-span-2 text-[#1A1815]">{b.date}<div className="text-xs text-[#6B665E]">{b.time}</div></div>
                <div className="col-span-2 text-[#C5A059] font-serif text-lg">£{Number(b.price).toLocaleString()}</div>
                <div className={`col-span-1 text-xs uppercase tracking-[0.16em] ${b.payment_status === "paid" ? "text-green-700" : "text-[#6B665E]"}`}>{b.payment_status}</div>
                <div className="col-span-2">
                  <Select value={b.status} onValueChange={(v) => update(b.id, v)}>
                    <SelectTrigger className="h-9 bg-white border-[#E4D9C1] text-[#1A1815]" data-testid={`admin-status-${b.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s} data-testid={`admin-status-option-${b.id}-${s}`}>{s.replace("_", " ")}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
            ))
          )}
        </div>
        </>)}

        {tab === "partners" && (
          <div className="mt-6 bg-white border border-[#E4D9C1]" data-testid="admin-partners">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E4D9C1] text-[10px] uppercase tracking-[0.22em] text-[#6B665E]">
              <div className="col-span-3">Applicant</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-2">Sector</div>
              <div className="col-span-2">Prefs</div>
              <div className="col-span-2">Status</div>
            </div>
            {partnersLoading ? (
              <div className="p-10 text-[#6B665E]">Loading…</div>
            ) : partners.length === 0 ? (
              <div className="p-10 text-[#6B665E]">No applications yet.</div>
            ) : (
              partners.map((p) => (
                <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#E4D9C1] items-start text-sm last:border-b-0" data-testid={`partner-row-${p.id}`}>
                  <div className="col-span-3">
                    <div className="text-[#1A1815] font-medium">{p.full_name}</div>
                    <div className="text-xs text-[#6B665E]">{new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="col-span-3">
                    <a href={`mailto:${p.email}`} className="text-[#1A1815] hover:text-[#C5A059] block">{p.email}</a>
                    <a href={`tel:${p.phone}`} className="text-xs text-[#6B665E] hover:text-[#C5A059]">{p.phone}</a>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[#1A1815] capitalize">{p.sector.replace("-", " ")}</div>
                    {p.sector_other && <div className="text-xs text-[#6B665E]">{p.sector_other}</div>}
                    {p.message && <div className="text-xs text-[#6B665E] mt-1 line-clamp-2">{p.message}</div>}
                  </div>
                  <div className="col-span-2 text-xs text-[#6B665E]">
                    <div>Avail: <span className="text-[#1A1815] capitalize">{p.availability}</span></div>
                    <div>Prefer: <span className="text-[#1A1815] capitalize">{p.contact_preference}</span></div>
                  </div>
                  <div className="col-span-2">
                    <Select value={p.status} onValueChange={(v) => updatePartner(p.id, v)}>
                      <SelectTrigger className="h-9 bg-white border-[#E4D9C1] text-[#1A1815]" data-testid={`partner-status-${p.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{APP_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`border p-6 ${highlight ? "border-[#C5A059] bg-[#C5A059]/10" : "border-[#E4D9C1] bg-white"}`}>
      <div className="overline">{label}</div>
      <div className={`font-serif text-3xl mt-2 ${highlight ? "text-[#8B6D2E]" : "text-[#1A1815]"}`}>{value}</div>
    </div>
  );
}
